import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Skeleton,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import { useState } from 'react';
import type { Group } from '@penticton-dev-events/shared';
import { useAuth } from '../../../app/providers/AuthProvider';

export type GroupListProps = {
  readonly groups: Group[];
  readonly onSelect?: (group: Group) => void;
};

type GroupCardImageProps = {
  readonly group: Group;
};

const GroupCardImage = ({ group }: GroupCardImageProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  if (!group.posterUrl || error) {
    // Fallback gradient when no poster or error loading
    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingTop: '56.25%', // 16:9 aspect ratio
          background: theme =>
            `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.8)} 0%, ${alpha(theme.palette.secondary.main, 0.6)} 100%)`,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 3,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: 'white',
              textAlign: 'center',
              fontWeight: 600,
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            }}
          >
            {group.name}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%', // 16:9 aspect ratio
        overflow: 'hidden',
        backgroundColor: 'grey.200',
      }}
    >
      {loading && (
        <Skeleton
          variant="rectangular"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
        />
      )}
      <Box
        component="img"
        src={group.posterUrl}
        alt={group.posterAlt || `${group.name} poster`}
        onLoad={() => setLoading(false)}
        onError={() => {
          setLoading(false);
          setError(true);
        }}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: loading ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out',
        }}
      />
      {/* Gradient overlay for better text readability */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '50%',
          background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)',
        }}
      />
      {/* Title overlay on image */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: 'white',
            fontWeight: 600,
            textShadow: '0 2px 8px rgba(0,0,0,0.5)',
          }}
        >
          {group.name}
        </Typography>
      </Box>
    </Box>
  );
};

const GroupList = ({ groups, onSelect }: GroupListProps) => {
  const { user } = useAuth();

  if (groups.length === 0) {
    return (
      <Stack alignItems="center" paddingY={8} spacing={2}>
        <Typography variant="h6">No groups yet</Typography>
        <Typography color="text.secondary" maxWidth={420} textAlign="center">
          Create the first group to start organizing meetups for your community.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="row" flexWrap="wrap" gap={3} justifyContent="flex-start">
      {groups.map(group => (
        <Card
          key={group.id}
          sx={{
            flexBasis: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33% - 16px)' },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme => theme.shadows[8],
            },
          }}
        >
          <CardActionArea
            onClick={() => onSelect?.(group)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              height: '100%',
            }}
          >
            <GroupCardImage group={group} />

            <CardContent sx={{ flexGrow: 1 }}>
              <Stack spacing={1.5}>
                {/* Organizer and Auto-accept badge */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  {user && (
                    <Typography variant="caption" color="text.secondary">
                      Managed by {group.organizerName}
                    </Typography>
                  )}
                  {group.autoAcceptMembers && (
                    <Chip
                      color="success"
                      label="Auto accept"
                      size="small"
                    />
                  )}
                </Stack>

                {/* Summary */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minHeight: '2.5em', // Reserve space for 2 lines
                  }}
                >
                  {group.summary ?? 'No description provided.'}
                </Typography>

                {/* Member count */}
                {typeof group.memberCount === 'number' && (
                  <Typography color="text.secondary" variant="caption">
                    {group.memberCount} {group.memberCount === 1 ? 'member' : 'members'}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </CardActionArea>
        </Card>
      ))}
    </Stack>
  );
};

export default GroupList;
